import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useModalStack } from '../../context/ModalStackContext';

const ModalPortal = ({ children, isOpen, modalId }) => {
  const { pushModal, popModal, getModalZIndex } = useModalStack();
  const modalIdRef = useRef(modalId || `modal-${Date.now()}-${Math.random()}`);
  const zIndexRef = useRef(null);

  // Generate stable modal ID if not provided
  const stableModalId = modalIdRef.current;

  useEffect(() => {
    if (isOpen) {
      // Push modal onto stack and get z-index
      zIndexRef.current = pushModal(stableModalId);
    } else if (zIndexRef.current !== null) {
      // Pop modal from stack when closing
      popModal(stableModalId);
      zIndexRef.current = null;
    }

    return () => {
      // Cleanup on unmount - ensure modal is removed from stack
      if (zIndexRef.current !== null) {
        popModal(stableModalId);
      }
    };
  }, [isOpen, stableModalId, pushModal, popModal]);

  if (!isOpen) return null;

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {
    console.error('Modal root element not found');
    return null;
  }

  // Clone children and inject z-index if it's a valid React element
  // #32: only inject onto COMPONENT children. A host element child
  // (typeof type === 'string', e.g. a plain <div>) would receive
  // modalZIndex/modalId as unknown DOM attributes — a React warning and
  // junk markup. Components (ImagePickerModal's content reads
  // modalZIndex) still get them.
  const childrenWithZIndex =
    React.isValidElement(children) && typeof children.type !== 'string'
      ? React.cloneElement(children, {
          modalZIndex: zIndexRef.current,
          modalId: stableModalId
        })
      : children;

  return ReactDOM.createPortal(childrenWithZIndex, modalRoot);
};

export default ModalPortal;